import axios from 'axios';
import { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../utils/constants';
import { sanitizeErrorMessage, validateApiUrl } from '../utils/security';
import type {
  SignupRequest,
  SigninRequest,
  OnboardingRequest,
  ForgotPasswordRequest,
  AuthResponse,
  OnboardingStatusResponse,
  TokenVerificationResponse,
  OnboardingResponse,
  ForgotPasswordResponse,
  LogoutResponse,
  UserUpdateRequest,
  UserUpdateResponse,
  UserProfileResponse,
  UserSessionResponse,
  CreatePatientRequest,
  CreatePatientResponse,
  GetPatientsRequest,
  GetPatientsResponse,
  UpdatePatientRequest,
  UpdatePatientResponse,
  DeletePatientResponse,
  CreatePayerRequest,
  CreatePayerResponse,
  GetPayersRequest,
  GetPayersResponse,
  UpdatePayerRequest,
  UpdatePayerResponse,
  DeletePayerResponse,
  CreateClaimRequest,
  CreateClaimResponse,
  GetClaimsRequest,
  GetClaimsResponse,
  GetClaimByIdResponse,
  UpdateClaimRequest,
  UpdateClaimResponse,
  DeleteClaimResponse,
  UpdateClaimStatusResponse,
  GetCallsRequest,
  GetCallsResponse,
  AiCallingStatusResponse,
  ToggleAiCallingRequest,
  ToggleAiCallingResponse,
  ManualCallRequest,
  ManualCallResponse,
  DashboardOverviewResponse,
  BulkUploadClaimsResponse,
} from './types';

// Validate API URL in production (must use HTTPS)
if (process.env.NODE_ENV === 'production' && !validateApiUrl(API_BASE_URL)) {
  throw new Error('API_BASE_URL must use HTTPS in production');
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log the active base URL at startup to help debugging
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line no-console
  console.info('[api] API base URL:', API_BASE_URL);
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[api] Request:', config.method, config.url, config.params || config.data);
      // Don't log headers in production to avoid exposing tokens
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // Log error in development so you see API errors in console without opening Network tab
    if (process.env.NODE_ENV === 'development') {
      const data = error.response?.data as { message?: string; detail?: string; field_errors?: Record<string, string> } | undefined;
      // eslint-disable-next-line no-console
      console.error('[api] Response error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message: data?.message ?? data?.detail ?? error.message,
        ...(data?.field_errors && { field_errors: data.field_errors }),
      });
    }

    if (error.response?.status === 401) {
      // Don't redirect on auth endpoints (login, signup, etc.) - let the component handle the error
      const isAuthEndpoint = error.config?.url?.includes('/auth/');
      
      if (!isAuthEndpoint) {
        // Clear token and redirect to login on unauthorized (only for protected routes)
        sessionStorage.removeItem('authToken');
        // Keep the redirect but log it so we can trace where it happened
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[api] 401 received — redirecting to /auth/login');
        }
        window.location.href = '/auth/login';
      }
    }

    // Sanitize error before rejecting
    const sanitizedError = {
      ...error,
      message: sanitizeErrorMessage(error),
    };
    return Promise.reject(sanitizedError);
  }
);

export default api;

// --- API wrapper functions ---
export const signup = async (data: SignupRequest): Promise<AuthResponse> => {
  const response = await api.post(API_ENDPOINTS.auth.signup, data);
  return response.data;
};

export const signin = async (data: SigninRequest): Promise<AuthResponse> => {
  const response = await api.post(API_ENDPOINTS.auth.signin, data);
  return response.data;
};

export const checkEmailVerification = async (): Promise<TokenVerificationResponse> => {
  const response = await api.get(API_ENDPOINTS.auth.checkEmailVerification);
  return response.data;
};


export const checkOnboardingStatus = async (): Promise<OnboardingStatusResponse> => {
  const response = await api.get(API_ENDPOINTS.auth.onboardingStatus);
  return response.data;
};

export const submitOnboarding = async (data: OnboardingRequest): Promise<OnboardingResponse> => {
  const response = await api.post(API_ENDPOINTS.auth.onboarding, data);
  return response.data;
};

export const forgotPassword = async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
  const response = await api.post(API_ENDPOINTS.auth.forgotPassword, data);
  return response.data;
};

export const logout = async (): Promise<LogoutResponse> => {
  const response = await api.post(API_ENDPOINTS.auth.logout);
  return response.data;
};

export const checkSession = async (): Promise<{ success: boolean; message: string; data: { valid: boolean } }> => {
  const response = await api.get(API_ENDPOINTS.users.session);
  return response.data;
};

export const fetchUserById = async (userId: number) => {
  const response = await api.get(`/users/${userId}`);
  return response.data;
};

export const updateUser = async (data: UserUpdateRequest): Promise<UserUpdateResponse> => {
  const response = await api.post(API_ENDPOINTS.users.update, data);
  return response.data;
};

export const getUserProfile = async (): Promise<UserProfileResponse> => {
  const response = await api.get(API_ENDPOINTS.users.me);
  return response.data;
};

export const getUserSession = async (): Promise<UserSessionResponse> => {
  const response = await api.get(API_ENDPOINTS.users.session);
  return response.data;
};

// Patient API functions
export const createPatient = async (data: CreatePatientRequest): Promise<CreatePatientResponse> => {
  const response = await api.post(API_ENDPOINTS.patients.create, data);
  return response.data;
};

export const getPatients = async (params?: GetPatientsRequest): Promise<GetPatientsResponse> => {
  const response = await api.get(API_ENDPOINTS.patients.list, { params });
  return response.data;
};

export const updatePatient = async (patientId: string, data: UpdatePatientRequest): Promise<UpdatePatientResponse> => {
  const response = await api.put(API_ENDPOINTS.patients.update(patientId), data);
  return response.data;
};

export const deletePatient = async (patientId: string): Promise<DeletePatientResponse> => {
  const response = await api.delete(API_ENDPOINTS.patients.delete(patientId));
  return response.data;
};

// Payer API functions
export const createPayer = async (data: CreatePayerRequest): Promise<CreatePayerResponse> => {
  const response = await api.post(API_ENDPOINTS.payers.create, data);
  return response.data;
};

export const getPayers = async (params?: GetPayersRequest): Promise<GetPayersResponse> => {
  const response = await api.get(API_ENDPOINTS.payers.list, { params });
  return response.data;
};

export const updatePayer = async (payerId: string, data: UpdatePayerRequest): Promise<UpdatePayerResponse> => {
  const response = await api.put(API_ENDPOINTS.payers.update(payerId), data);
  return response.data;
};

export const deletePayer = async (payerId: string): Promise<DeletePayerResponse> => {
  const response = await api.delete(API_ENDPOINTS.payers.delete(payerId));
  return response.data;
};

// Claim API functions
export const createClaim = async (data: CreateClaimRequest): Promise<CreateClaimResponse> => {
  const response = await api.post(API_ENDPOINTS.claims.create, data);
  return response.data;
};

export const getClaims = async (params?: GetClaimsRequest): Promise<GetClaimsResponse> => {
  const response = await api.get(API_ENDPOINTS.claims.list, { params });
  return response.data;
};

export const getClaimById = async (claimId: string): Promise<GetClaimByIdResponse> => {
  const response = await api.get(API_ENDPOINTS.claims.detail(claimId));
  return response.data;
};

export const updateClaim = async (claimId: string, data: UpdateClaimRequest): Promise<UpdateClaimResponse> => {
  const response = await api.put(API_ENDPOINTS.claims.update(claimId), data);
  return response.data;
};

export const deleteClaim = async (claimId: string): Promise<DeleteClaimResponse> => {
  const response = await api.delete(API_ENDPOINTS.claims.delete(claimId));
  return response.data;
};

export const updateClaimStatus = async (claimId: string, status: string): Promise<UpdateClaimStatusResponse> => {
  const response = await api.patch(API_ENDPOINTS.claims.updateStatus(claimId), null, {
    params: { status }
  });
  return response.data;
};

// Twilio AI Calling API functions
export const getAiCallingStatus = async (): Promise<AiCallingStatusResponse> => {
  const response = await api.get(API_ENDPOINTS.twilio.aiCallingStatus);
  return response.data;
};

export const toggleAiCalling = async (data: ToggleAiCallingRequest): Promise<ToggleAiCallingResponse> => {
  const response = await api.patch(API_ENDPOINTS.twilio.toggleAiCalling, data);
  return response.data;
};

// Calls API functions
export const getCalls = async (params?: GetCallsRequest): Promise<GetCallsResponse> => {
  const response = await api.get(API_ENDPOINTS.calls.list, { params });
  return response.data;
};

// Manual Call API function
export const placeManualCall = async (data: ManualCallRequest): Promise<ManualCallResponse> => {
  const response = await api.post(API_ENDPOINTS.calls.manual, data);
  return response.data;
};

// Dashboard API function
export const getDashboardOverview = async (): Promise<DashboardOverviewResponse> => {
  const response = await api.get(API_ENDPOINTS.dashboard.overview);
  return response.data;
};

// --- Bulk upload (claims) ---
export const downloadExampleClaimUploadFile = async (
  format: 'csv' | 'xlsx' = 'csv'
): Promise<Blob> => {
  const response = await api.get(API_ENDPOINTS.claims.bulkUploadExample, {
    params: { format },
    responseType: 'blob',
  });
  return response.data as Blob;
};

export const bulkUploadClaims = async (file: File): Promise<BulkUploadClaimsResponse> => {
  const form = new FormData();
  form.append('file', file);

  const response = await api.post(API_ENDPOINTS.claims.bulkUpload, form, {
    // Let the browser/axios set the multipart boundary automatically
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};
